/**
 * Hooks for Activity and ActivityLog queries
 * Replaces static API methods with react-query hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from './useBackend';
import { ActivityLog, newActivityLog, toActivityLogPayload } from '../Model/Activity/ActivityLog';
import { Activity, createActivity } from '../Model/Activity/Activity';
import type { Pageable } from '../Model/Interfaces/Pageable';

// Activity Hooks
export function useActivity(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['activity', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/activity/${id}`);
      return json ? createActivity(json) : undefined;
    },
    enabled: !!id,
    staleTime: 30 * 60 * 1000,
  });
}

// ActivityLog Hooks
export function useActivityLog(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['activity-log', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/activity/tracking/${id}`);
      return json ? newActivityLog(json) : undefined;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useActivityLogs(params?: Partial<Pageable> & { athleteId?: string }) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['activity-logs', params],
    queryFn: async () => {
      const json = await backend.get('/activity/tracking', params);
      return (json ?? []).map((a: any) => newActivityLog(a));
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useActivityRoute(activityLogId?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['activity-route', activityLogId],
    queryFn: async () => {
      if (!activityLogId) return [];
      const json = await backend.get(`/activity/tracking/${activityLogId}/route`);
      return json ?? [];
    },
    enabled: !!activityLogId,
    staleTime: 10 * 60 * 1000,
  });
}

// Activity Mutations
export function useSaveActivityLog() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityLog: Partial<ActivityLog>) =>
      backend.post('/activity/tracking', toActivityLogPayload(activityLog as ActivityLog)),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ['activity-log', variables.id] });
      }
    },
  });
}

export function useDeleteActivityLog() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityLogId: string) =>
      backend.delete(`/activity/tracking/${activityLogId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
    },
  });
}
